import React, { useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogTitle,
  Button,
  IconButton,
  Input,
  MultiSelect,
  Icon
} from '@shopflo/ui'
import { X, Lightbulb, CaretDown, Info, BookOpenText, Repeat } from '@phosphor-icons/react'
import { DownloadReportConfig, ExportFrequency } from 'src/components/common/export-component/types'
import ExportReportOnce from 'src/components/common/export-component/steps/two/once'
import RecurringScheduleFields, { getScheduleNote } from 'src/components/Reports/RecurringScheduleFields'
import ExportDataSelect from 'src/components/Reports/ExportDataSelect'
import { useReports } from 'src/contexts/ReportContext'
import { useLocale } from 'src/lib/hooks/useLocal'
import { CreateReportDialogProps } from 'src/components/Reports/types'

const CreateReportDialog: React.FC<CreateReportDialogProps> = ({
  isOpen,
  onClose,
  isEdit = false,
  config,
  onConfigChange,
  columns,
  isLoadingColumns,
  isSubmitting,
  onSubmit
}) => {
  const { t } = useLocale()
  const { emails, isValidating: isLoadingEmails } = useReports()
  const [showProTip, setShowProTip] = useState(true)
  const [isOnceRangeValid, setIsOnceRangeValid] = useState(true)

  const emailOptions = useMemo(() => emails.map((email: string) => ({ value: email, label: email })), [emails])

  const selectedColumns = config.step1.selectedColumns as string[]
  const selectedEmails = config.step2.emails ?? []
  const frequency = config.step2.exportFrequency
  const isScheduleValid = frequency !== 'once' || isOnceRangeValid

  const canSubmit = isEdit
    ? // Require columns even in edit mode — prevents Saving with an empty set before the detail
      // fetch has prefilled columns (which would wipe them via scheduler-status).
      !!config.step2.reportName?.trim() &&
      selectedColumns.length > 0 &&
      isScheduleValid &&
      !isSubmitting &&
      !isLoadingColumns
    : !!config.step2.reportName?.trim() &&
      selectedColumns.length > 0 &&
      isScheduleValid &&
      !isSubmitting &&
      !isLoadingColumns

  const updateStep1 = (columnsValue: string[]) =>
    onConfigChange(prev => ({ ...prev, step1: { ...prev.step1, selectedColumns: columnsValue } }))

  const updateStep2 = (patch: Partial<DownloadReportConfig['step2']>) =>
    onConfigChange(prev => ({ ...prev, step2: { ...prev.step2, ...patch } }))

  return (
    <Dialog open={isOpen} onOpenChange={open => !open && onClose()}>
      <DialogOverlay className='fixed inset-0 z-40 bg-black/40' />
      <DialogContent className='z-50 w-[600px] max-w-[92vw] gap-0 overflow-hidden !rounded-2xl bg-white p-0 shadow-modal'>
        <DialogHeader className='flex flex-row items-center justify-between p-5 pb-4'>
          <DialogTitle className='text-text-lg-semibold text-zinc-900'>
            {isEdit ? t('report.dialog.title_edit') : t('report.dialog.title_create')}
          </DialogTitle>
          <IconButton IconComponent={X} size='sm' variant='ghost' className='text-zinc-400' onClick={onClose} />
        </DialogHeader>

        {/* Top section — report definition */}
        <div className='flex flex-col gap-5 px-5 pb-6'>
          <div className='flex flex-col gap-1.5'>
            <label className='text-text-xs-medium text-zinc-500'>{t('report.dialog.report_title')}</label>
            <Input
              variant='outline'
              dimension='md'
              placeholder={t('report.dialog.report_name_placeholder')}
              value={config.step2.reportName}
              maxLength={60}
              onChange={e => updateStep2({ reportName: e.target.value })}
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <div className='flex items-center justify-between'>
              <label className='text-text-xs-medium text-zinc-500'>{t('report.dialog.export_data')}</label>
              {selectedColumns.length > 0 && (
                <span className='text-text-xs-regular text-zinc-500'>
                  {selectedColumns.length} {t('report.dialog.fields')}
                </span>
              )}
            </div>
            <ExportDataSelect
              columns={columns}
              value={selectedColumns}
              onChange={updateStep1}
              isLoading={isLoadingColumns}
            />
          </div>

          <div className='flex flex-col gap-1.5'>
            <label className='text-text-xs-medium text-zinc-500'>{t('report.dialog.share_with')}</label>
            <MultiSelect
              options={emailOptions}
              value={selectedEmails}
              onValueChange={value => updateStep2({ emails: value })}
              placeholder={isLoadingEmails ? t('report.dialog.recipients_loading') : t('report.dialog.recipients_placeholder')}
              searchable
              size='md'
              endIcon={<Icon IconComponent={CaretDown} size='sm' className='text-zinc-400' />}
            />
          </div>
        </div>

        {/* Bottom section — schedule */}
        <div className='flex flex-col gap-3 border-t border-zinc-200 bg-zinc-50 px-5 pb-8 pt-5'>
          {!isEdit && (
            <>
              {showProTip && (
                <div className='flex items-center gap-1 rounded-lg border border-sky-100 bg-sky-50 px-1 py-1.5'>
                  <Icon IconComponent={Lightbulb} size='sm' className='shrink-0 text-sky-500' iconProps={{ weight: 'fill' }} />
                  <p className='flex-1 text-text-xs-regular text-zinc-600'>
                    <span className='text-text-xs-semibold text-zinc-700'>{t('report.dialog.pro_tip_label')}</span>{' '}
                    {t('report.dialog.pro_tip_text')}
                  </p>
                  <IconButton
                    IconComponent={X}
                    size='xs'
                    variant='ghost'
                    className='shrink-0 text-zinc-400'
                    onClick={() => setShowProTip(false)}
                  />
                </div>
              )}

              <div className='flex w-full items-center gap-1 rounded-xl bg-zinc-100 p-1'>
                {(
                  [
                    { value: 'once', label: t('report.dialog.export_once') },
                    { value: 'recurring', label: t('report.dialog.recurring') }
                  ] as { value: ExportFrequency; label: string }[]
                ).map(item => {
                  const active = frequency === item.value
                  return (
                    <button
                      key={item.value}
                      type='button'
                      onClick={() => updateStep2({ exportFrequency: item.value })}
                      className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-1 text-text-sm-medium transition-all ${
                        active ? 'bg-white text-zinc-800 shadow-ring' : 'text-zinc-500 hover:text-zinc-700'
                      }`}
                    >
                      {item.value === 'once' ? (
                        <Icon
                          IconComponent={BookOpenText}
                          size='md'
                          className='shrink-0 text-zinc-500'
                          iconProps={{ weight: 'duotone' }}
                        />
                      ) : (
                        <Icon
                          IconComponent={Repeat}
                          size='md'
                          className='shrink-0 text-zinc-500'
                          iconProps={{ weight: 'duotone' }}
                        />
                      )}
                      {item.label}
                    </button>
                  )
                })}
              </div>
            </>
          )}

          {frequency === 'once' ? (
            <ExportReportOnce
              variant='reports'
              config={config.step2}
              onChange={next => onConfigChange(prev => ({ ...prev, step2: next }))}
              onValidationChange={setIsOnceRangeValid}
            />
          ) : (
            <RecurringScheduleFields
              schedule={config.step2.schedule}
              onChange={schedule =>
                onConfigChange(prev => ({
                  ...prev,
                  step2: { ...prev.step2, schedule, dateRange: { startDate: '', endDate: '' } }
                }))
              }
            />
          )}
        </div>

        <DialogFooter className='flex flex-row items-center justify-between gap-2 bg-zinc-50 px-5 pb-4'>
          {frequency === 'recurring' ? (
            <div className='flex items-center gap-1'>
              <Icon IconComponent={Info} size='xs' className='text-zinc-400' iconProps={{ weight: 'duotone' }} />
              <span className='text-text-xs-regular text-zinc-500'>{getScheduleNote(config.step2.schedule)}</span>
            </div>
          ) : (
            <span />
          )}
          <div className='flex items-center gap-2'>
            <Button variant='outline' size='md' onClick={onClose}>
              {t('report.dialog.cancel')}
            </Button>
            <Button variant='primary' size='md' loading={isSubmitting} disabled={!canSubmit} onClick={onSubmit}>
              {isEdit ? t('report.dialog.save_changes') : t('report.dialog.title_create')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default CreateReportDialog
